import React, { FC, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  makeStyles,
  Box,
  AccordionSummary,
  Accordion,
  AccordionDetails,
  Tabs,
  Tab,
} from "@material-ui/core";

import { Chart } from "../components/Chart";
import { CallToAction } from "../components/CallToAction";
import { store } from "../store";
import { Text } from "../components/Text";
import Model from "../components/Model";
import Button from "../components/Button";
import { Link } from "react-router-dom";
import InputParameters from "../components/InputParameters";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { NextButton } from "../components/NextButton";
import { Loading } from "../components/Loading";
import DriverSelect from "../components/DriverSelect";
import { useIdentityContext } from "react-netlify-identity";
import UserDesigns from "../components/Designs";
import { defaultColors } from "../constants/colors";
import { Alert } from "../components/Alert";

export interface ParameterState {
  horizontalBeamwidth: number | null;
  verticalBeamwidth: number | null;
  horizontalMax: number | null;
  verticalMax: number | null;
  upperFrequency: number | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = ({ children, value, index, ...rest }: TabPanelProps) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`scrollable-auto-tabpanel-${index}`}
    aria-labelledby={`scrollable-auto-tab-${index}`}
    {...rest}
  >
    {value === index && (
      <Box display="flex" justifyContent="center" marginTop={2}>
        {children}
      </Box>
    )}
  </div>
);

const a11yProps = (index: number) => ({
  id: `scrollable-auto-tab-${index}`,
  "aria-controls": `scrollable-auto-tabpanel-${index}`,
});

const Results: FC = observer(() => {
  const classes = styles();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(0);

  const { user } = useIdentityContext();
  const {
    results,
    beamwidth,
    impedance,
    frequencyResponse,
    directivity,
    appLoading,
    setLoading,
    models,
    setUser,
    calculateHornDesign,
    missingRequiredParams,
  } = store;
  const reSubmit = async () => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
    setOpen(!open);
    setLoading(true);
    const tokenExpires = user?.tokenDetails().expires_at;
    if (tokenExpires < new Date().getTime()) {
      await user?.jwt(true);
    }
    setUser(user);
    await calculateHornDesign();
    setLoading(false);
  };

  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setValue(newValue);
  };

  if (results)
    return (
      <Box className={classes.wrapper}>
        <Box className={classes.content}>
          <Box marginBottom={2}>
            <Accordion expanded={open} onChange={() => setOpen(!open)}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Text color="secondary" bold>
                  Parameters
                </Text>
              </AccordionSummary>
              <AccordionDetails
                style={{ display: "flex", flexDirection: "column" }}
              >
                <InputParameters dynamic>
                  <DriverSelect />
                </InputParameters>

                <Box className={classes.container}>
                  <NextButton
                    disabled={missingRequiredParams}
                    text="Calculate again"
                    onClick={reSubmit}
                  />
                </Box>

                <UserDesigns />
              </AccordionDetails>
            </Accordion>
          </Box>

          {appLoading ? (
            <Box height={[200, 400]} width={1}>
              <Loading text="Calculating..." />
            </Box>
          ) : (
            <>
              {models.length === 1 ? (
                <Model
                  output={models?.[0].model}
                  driverName={models?.[0].driverName}
                  geometry={models?.[0].geometry}
                />
              ) : (
                <>
                  <Tabs
                    value={value}
                    onChange={handleChange}
                    TabIndicatorProps={{
                      style: {
                        backgroundColor: defaultColors[value],
                      },
                    }}
                    centered
                  >
                    {models.map((_, index) => (
                      <Tab
                        key={`Design ${index + 1}`}
                        style={{
                          color: value === index && defaultColors[value],
                        }}
                        label={`Design ${index + 1}`}
                        {...a11yProps(index)}
                      />
                    ))}
                  </Tabs>

                  {models.map((m, index) => {
                    const { model, driverName, geometry } = m;

                    return (
                      <TabPanel
                        key={`${model._id}-${Math.random()}`}
                        value={value}
                        index={index}
                      >
                        <Model
                          output={model}
                          driverName={driverName}
                          geometry={geometry}
                        />
                      </TabPanel>
                    );
                  })}
                </>
              )}
            </>
          )}
          <Box className={classes.container}>
            <Chart
              xLabel="Frequency [Hz]"
              yLabel="Beamwidth [Degrees]"
              data={beamwidth.data}
              title="BEAMWIDTH"
              xAxisLabels={beamwidth.xAxisLabels}
              yScale={{ min: 0, max: 180 }}
            />
            <Chart
              data={directivity.data}
              title="DIRECTIVITY INDEX"
              xLabel="Frequency [Hz]"
              yLabel="[dB]"
              xAxisLabels={beamwidth.xAxisLabels}
            />
            <Chart
              data={frequencyResponse.data}
              title="FREQUENCY RESPONSE"
              xLabel="Frequency [Hz]"
              yLabel="[dB]"
              xAxisLabels={beamwidth.xAxisLabels}
              yScale={{ min: 50, max: 120 }}
            />
            <Chart
              data={impedance.data}
              title="IMPEDANCE"
              xLabel="Frequency [Hz]"
              yLabel="Ohms [Ω]"
              xAxisLabels={beamwidth.xAxisLabels}
              yScale={{ min: 0, max: 40 }}
            />
          </Box>
        </Box>
        <CallToAction />

        <Alert />
      </Box>
    );

  return (
    <Box>
      <Box className={classes.noResults}>
        <Box className={classes.message}>
          <Text size="large" align="center" bold>
            Please run a simulation to generate results.
          </Text>
        </Box>
        <Link className={classes.link} to="/">
          <Button>HOME</Button>
        </Link>
      </Box>
      <Alert />
    </Box>
  );
});

const styles = makeStyles((theme) => ({
  wrapper: {
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    [theme.breakpoints.down("md")]: {
      display: "block",
    },
  },

  container: {
    flex: 1,
    maxWidth: "100vw",
    position: "relative",
    overflow: "auto",
    padding: "20px",
    margin: "0 auto",
    [theme.breakpoints.up("md")]: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-around",
      maxWidth: 1200,
      flexWrap: "wrap",
    },
    [theme.breakpoints.down("sm")]: {
      marginTop: 20,
      marginLeft: 15,
      padding: "0.5em",
      display: "grid",
    },
  },
  content: {
    padding: "1em 2em",
    maxWidth: 1200,
    [theme.breakpoints.down("sm")]: {
      padding: "1em 0.5em",
    },
  },
  noResults: {
    margin: "150px auto",
    maxWidth: 300,
  },
  message: {
    marginBottom: 60,
  },
  link: {
    width: 250,
    textDecoration: "none",
  },
}));

export default Results;
